import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Clock,
  Lightning,
  ChartLine,
  Receipt,
  Sparkle,
  TrendUp,
  Fire,
} from '@phosphor-icons/react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, ComposedChart, Line, PieChart, Pie, Cell,
} from 'recharts';

// ---------------------------------------------------------------------------
// Partner color palette (stable per partner_id)
// ---------------------------------------------------------------------------

const PARTNER_COLORS = {
  swiggy_delivery: '#f97316',   // orange
  zomato_delivery: '#ef4444',   // red
  inhouse_delivery: '#3b82f6',  // blue
  swiggy_dining:   '#f59e0b',   // amber
  zomato_dining:   '#dc2626',   // red-600
  inhouse_dine_in: '#10b981',   // emerald
  inhouse_takeaway: '#eab308',  // yellow
  unknown: '#71717a',           // zinc
};
const DEFAULT_COLOR = '#a855f7';
const colorFor = (pid) => PARTNER_COLORS[pid] || DEFAULT_COLOR;

const SERVICE_COLOR = {
  delivery: '#3b82f6',
  dine_in:  '#10b981',
  takeaway: '#f59e0b',
  other:    '#71717a',
  all:      '#e4e4e7',
};
const SERVICE_LABEL = {
  all: 'All',
  delivery: 'Delivery',
  dine_in: 'Dine-in',
  takeaway: 'Takeaway',
  other: 'Other',
};

const MODE_TOGGLES = ['all', 'delivery', 'dine_in', 'takeaway'];

// Static Tailwind class mapping for dynamic col-span
const SPAN_CLS = {
  4:  'md:col-span-4',
  6:  'md:col-span-6',
  8:  'md:col-span-8',
  12: 'md:col-span-12',
};

const fmtHourAmPm = (h) => {
  if (h == null) return '—';
  const ampm = h < 12 ? 'AM' : 'PM';
  const disp = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${disp} ${ampm}`;
};

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

const Card = ({ span = 4, children, className = '', testid }) => (
  <div
    className={`col-span-12 ${SPAN_CLS[span] || 'md:col-span-4'} surface-base rounded-xl border border-white/[0.06] p-4 ${className}`}
    data-testid={testid}
  >
    {children}
  </div>
);

const CardTitle = ({ icon: Icon, title, subtitle, color = 'text-cyan-400', action }) => (
  <div className="flex items-start justify-between mb-3">
    <div>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={14} weight="fill" className={color} />}
        <h3 className="text-xs font-medium text-zinc-200 uppercase tracking-wider">{title}</h3>
      </div>
      {subtitle && <p className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ---------------------------------------------------------------------------
// Helpers — filter partners by service_mode
// ---------------------------------------------------------------------------

const filterPartnersForMode = (allPartnerIds, mode, partnerServiceModeMap) => {
  if (mode === 'all') return allPartnerIds;
  return allPartnerIds.filter((pid) => partnerServiceModeMap[pid] === mode);
};

// Build a flat hourly series where each row has `hour` + one key per partner_id with the count
const buildChartSeries = (hourlyDemand, visiblePartners) => {
  return hourlyDemand.map((h) => {
    const row = { hour: h.hour };
    for (const pid of visiblePartners) row[pid] = 0;
    for (const p of h.partners || []) {
      if (visiblePartners.includes(p.partner_id)) {
        row[p.partner_id] = p.orders;
      }
    }
    return row;
  });
};

// ---------------------------------------------------------------------------
// Hourly Demand Hero (partner-aware areas)
// ---------------------------------------------------------------------------

const HourlyDemandHero = ({ hourlyDemand, hourlyPrepTime, visiblePartners, partnerMixById, activeMode, showPrepOverlay, onTogglePrepOverlay }) => {
  const chartData = useMemo(() => {
    // build base partner series
    const base = buildChartSeries(hourlyDemand, visiblePartners);
    // attach prep time per hour (filtered to active mode)
    const prepBy = {};
    for (const p of hourlyPrepTime || []) {
      let val = null;
      if (activeMode === 'delivery') val = p.delivery_avg_min;
      else if (activeMode === 'dine_in') val = p.dine_in_avg_min;
      else if (activeMode === 'takeaway') val = p.takeaway_avg_min;
      else val = p.overall_avg_min; // 'all'
      prepBy[p.hour] = val;
    }
    return base.map((row) => ({ ...row, prep_min: prepBy[row.hour] ?? null }));
  }, [hourlyDemand, hourlyPrepTime, visiblePartners, activeMode]);

  return (
    <Card span={12} testid="block-hourly-hero">
      <CardTitle
        icon={ChartLine}
        title="Orders by Hour"
        subtitle={showPrepOverlay ? 'Partner demand + prep time overlay' : 'Partner-level hourly demand'}
        color="text-cyan-400"
        action={
          <button
            onClick={onTogglePrepOverlay}
            className={`px-2.5 py-1 text-[10px] font-medium rounded border transition-colors ${
              showPrepOverlay
                ? 'bg-orange-500/15 border-orange-500/40 text-orange-300'
                : 'bg-white/[0.03] border-white/10 text-zinc-400 hover:text-zinc-200'
            }`}
            data-testid="toggle-prep-overlay"
          >
            Prep Time Overlay
          </button>
        }
      />
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              {visiblePartners.map((pid) => (
                <linearGradient key={pid} id={`g-${pid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colorFor(pid)} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={colorFor(pid)} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="hour" stroke="#52525b" fontSize={10} tickFormatter={(h) => fmtHourAmPm(h)} interval={2} />
            <YAxis yAxisId="left" stroke="#52525b" fontSize={10} allowDecimals={false} />
            {showPrepOverlay && (
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#fb923c"
                fontSize={10}
                tickFormatter={(v) => `${v}m`}
                allowDecimals={false}
              />
            )}
            <Tooltip
              contentStyle={{ backgroundColor: '#09090b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 11 }}
              labelFormatter={(h) => fmtHourAmPm(h)}
              formatter={(v, name) => {
                if (name === 'prep_min') return [v != null ? `${v} min` : '—', 'Avg Prep Time'];
                return [v, partnerMixById[name]?.partner_name || name];
              }}
            />
            {visiblePartners.map((pid) => (
              <Area
                key={pid}
                yAxisId="left"
                type="monotone"
                dataKey={pid}
                stroke={colorFor(pid)}
                strokeWidth={2}
                fill={`url(#g-${pid})`}
                stackId={undefined}
              />
            ))}
            {showPrepOverlay && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="prep_min"
                stroke="#fb923c"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                activeDot={{ r: 3 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-zinc-500">
        {visiblePartners.length === 0 ? (
          <span className="italic">No partners in this view</span>
        ) : (
          visiblePartners.map((pid) => (
            <span key={pid} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: colorFor(pid) }} />
              {partnerMixById[pid]?.partner_name || pid}
            </span>
          ))
        )}
        {showPrepOverlay && (
          <span className="flex items-center gap-1.5 ml-auto">
            <span className="w-6 h-[2px] border-b border-dashed border-orange-400" style={{ background: 'transparent', borderColor: '#fb923c' }} />
            <span className="text-orange-400">Prep time (min)</span>
          </span>
        )}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Peak Load — per partner
// ---------------------------------------------------------------------------

const PeakLoadCard = ({ peakByPartner, visiblePartners, overall }) => {
  const rows = (peakByPartner || []).filter((p) => visiblePartners.includes(p.partner_id));
  return (
    <Card span={4} testid="block-peak-load">
      <CardTitle icon={Fire} title="Peak Load" subtitle="Per-partner peak hour" color="text-red-400" />
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-500">No partner data in this view.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.slice(0, 6).map((p) => (
            <div key={p.partner_id} className="flex items-center justify-between text-xs" data-testid={`peak-row-${p.partner_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-5 rounded-sm shrink-0" style={{ background: colorFor(p.partner_id) }} />
                <span className="text-zinc-300 truncate">{p.partner_name}</span>
              </div>
              <div className="text-right shrink-0 ml-2">
                <div className="text-zinc-200 font-mono">{p.peak_hour != null ? fmtHourAmPm(p.peak_hour) : '—'}</div>
                <div className="text-[10px] text-zinc-500 font-mono">{p.orders} orders</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {overall?.hour != null && (
        <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-zinc-400 flex items-center gap-1.5">
          <Lightning size={11} weight="fill" className="text-amber-400" />
          Overall peak: <span className="text-zinc-200 font-mono">{fmtHourAmPm(overall.hour)}</span>
          <span className="text-zinc-500">({overall.orders} orders)</span>
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Time Window Selector — partner breakdown
// ---------------------------------------------------------------------------

const TimeWindowCard = ({ summary, visiblePartners, onChange, isDefaultPeak }) => {
  const [s, setS] = useState(summary?.start_hour ?? 8);
  const [e, setE] = useState(summary?.end_hour ?? 10);

  useEffect(() => {
    if (summary?.start_hour != null) setS(summary.start_hour);
    if (summary?.end_hour != null) setE(summary.end_hour);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.start_hour, summary?.end_hour]);

  const apply = () => {
    if (e <= s) {
      toast.error('End hour must be after start hour');
      return;
    }
    onChange?.(s, e);
  };

  const reset = () => onChange?.(null, null);
  const byPartnerFiltered = (summary?.by_partner || []).filter((p) => visiblePartners.includes(p.partner_id));

  return (
    <Card span={4} testid="block-time-window">
      <CardTitle icon={Clock} title="Time Window" subtitle={isDefaultPeak ? 'Default: auto peak 2-hour window' : 'Custom window'} color="text-amber-400" />
      <div className="flex items-end gap-2">
        <div className="flex flex-col flex-1">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">From</label>
          <select
            value={s}
            onChange={(ev) => setS(parseInt(ev.target.value, 10))}
            className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-amber-500/40"
            data-testid="window-start"
          >
            {Array.from({ length: 24 }).map((_, i) => (
              <option key={i} value={i}>{fmtHourAmPm(i)}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col flex-1">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">To</label>
          <select
            value={e}
            onChange={(ev) => setE(parseInt(ev.target.value, 10))}
            className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-amber-500/40"
            data-testid="window-end"
          >
            {Array.from({ length: 25 }).map((_, i) => (
              <option key={i} value={i}>{i === 24 ? '12 AM (next)' : fmtHourAmPm(i)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={apply}
          className="px-3 py-1 text-xs font-medium rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/15 transition-colors"
          data-testid="window-apply"
        >
          Apply
        </button>
      </div>

      {summary ? (
        <>
          <div className="mt-4 text-[11px] text-zinc-400">
            Total orders in window
            <div className="text-lg font-mono text-zinc-100 mt-0.5">
              {byPartnerFiltered.reduce((a, b) => a + b.orders, 0)}
              <span className="ml-1.5 text-[10px] text-zinc-500">/ {summary.total_orders}</span>
            </div>
          </div>
          <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {byPartnerFiltered.length === 0 ? (
              <div className="text-xs text-zinc-500">No partner traffic in this window.</div>
            ) : (
              byPartnerFiltered.map((row) => (
                <div key={row.partner_id} className="flex items-center justify-between text-[11px]">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="w-1.5 h-3 rounded-sm shrink-0" style={{ background: colorFor(row.partner_id) }} />
                    <span className="text-zinc-400 truncate">{row.partner_name}</span>
                  </span>
                  <span className="font-mono text-zinc-300 shrink-0 ml-2">
                    {row.orders} <span className="text-zinc-600">({row.share_pct}%)</span>
                  </span>
                </div>
              ))
            )}
          </div>
          {!isDefaultPeak && (
            <button onClick={reset} className="mt-3 text-[10px] text-zinc-500 hover:text-zinc-300 underline" data-testid="window-reset">
              Reset to peak window
            </button>
          )}
        </>
      ) : (
        <div className="mt-3 text-xs text-zinc-500">No data for this window.</div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Filter-aware insights (regenerates on mode/window/partner changes)
// ---------------------------------------------------------------------------

const buildFilteredInsights = ({ hourlyDemand, partnerMix, visiblePartners, activeMode, windowSummary, hourlyPrepTime, partnerNames }) => {
  const visibleSet = new Set(visiblePartners);
  const totalOrders = (partnerMix || []).filter((p) => visibleSet.has(p.partner_id)).reduce((a, b) => a + b.orders, 0);

  // Insufficient data gate
  if (!totalOrders || totalOrders < 3) {
    return [{
      type: 'insufficient',
      text: 'Not enough data for strong insights in this filter range.',
    }];
  }

  const insights = [];

  // 1) Dominant partner in the current filtered view
  const filteredMix = (partnerMix || []).filter((p) => visibleSet.has(p.partner_id));
  if (filteredMix.length > 0) {
    const top = [...filteredMix].sort((a, b) => b.orders - a.orders)[0];
    const pct = totalOrders ? Math.round((top.orders / totalOrders) * 100) : 0;
    if (pct >= 30) {
      insights.push({
        type: 'dominance',
        text: `${top.partner_name} dominates this view — ${pct}% of filtered orders (${top.orders}).`,
      });
    }
  }

  // 2) Peak hour / peak window of the filtered demand
  const hourTotals = (hourlyDemand || []).map((row) => {
    let t = 0;
    for (const p of row.partners || []) if (visibleSet.has(p.partner_id)) t += p.orders;
    return { hour: row.hour, total: t };
  });
  const peak = hourTotals.reduce((a, b) => (b.total > a.total ? b : a), { hour: null, total: 0 });
  if (peak.total > 0 && peak.hour != null) {
    const label = `${fmtHourAmPm(peak.hour)}–${fmtHourAmPm((peak.hour + 1) % 24)}`;
    insights.push({
      type: 'peak_hour',
      text: `Demand peaks between ${label} (${peak.total} orders at the hour).`,
    });
  }

  // 3) Concentration insight — % of filtered orders in the top 3 contiguous hours
  if (totalOrders > 5) {
    let bestStart = 0, bestSum = 0;
    for (let s = 0; s < 22; s++) {
      const sum = hourTotals[s].total + hourTotals[s + 1].total + hourTotals[s + 2].total;
      if (sum > bestSum) { bestSum = sum; bestStart = s; }
    }
    const pct = Math.round((bestSum / totalOrders) * 100);
    if (pct >= 40) {
      insights.push({
        type: 'concentration',
        text: `${pct}% of orders concentrate in a 3-hour window (${fmtHourAmPm(bestStart)}–${fmtHourAmPm(bestStart + 3)}).`,
      });
    }
  }

  // 4) Window-specific (if a user-selected window exists)
  if (windowSummary && windowSummary.by_partner) {
    const relevant = windowSummary.by_partner.filter((p) => visibleSet.has(p.partner_id));
    const wTotal = relevant.reduce((a, b) => a + b.orders, 0);
    if (wTotal >= 3 && relevant.length > 0) {
      const topW = [...relevant].sort((a, b) => b.orders - a.orders)[0];
      const pct = Math.round((topW.orders / wTotal) * 100);
      insights.push({
        type: 'window',
        text: `In ${windowSummary.label}: ${topW.partner_name} leads with ${pct}% (${topW.orders}/${wTotal} orders).`,
      });
    }
  }

  // 5) Operational — bottleneck hour (only when overlay data exists)
  if (hourlyPrepTime && hourlyPrepTime.length) {
    let worst = null;
    for (const p of hourlyPrepTime) {
      let val = null;
      if (activeMode === 'delivery') val = p.delivery_avg_min;
      else if (activeMode === 'dine_in') val = p.dine_in_avg_min;
      else if (activeMode === 'takeaway') val = p.takeaway_avg_min;
      else val = p.overall_avg_min;
      if (val != null && (!worst || val > worst.val)) worst = { hour: p.hour, val };
    }
    if (worst && worst.val > 45) {
      insights.push({
        type: 'bottleneck',
        text: `Bottleneck risk at ${fmtHourAmPm(worst.hour)} — avg ${Math.round(worst.val)} min prep time.`,
      });
    }
  }

  // 6) Partner shift — compare to overall peak (multi-partner case only, in 'all' or partner-filtered modes with 2+)
  if (visiblePartners.length >= 2) {
    // find each partner's peak hour
    const peaks = {};
    for (const pid of visiblePartners) peaks[pid] = { h: null, c: 0 };
    for (const row of hourlyDemand || []) {
      for (const p of row.partners || []) {
        if (visibleSet.has(p.partner_id) && p.orders > peaks[p.partner_id].c) {
          peaks[p.partner_id] = { h: row.hour, c: p.orders };
        }
      }
    }
    const entries = Object.entries(peaks).filter(([, v]) => v.h != null && v.c > 0);
    if (entries.length >= 2) {
      entries.sort((a, b) => a[1].h - b[1].h);
      const earliest = entries[0];
      const latest = entries[entries.length - 1];
      if (latest[1].h - earliest[1].h >= 2) {
        const eName = partnerNames[earliest[0]] || earliest[0];
        const lName = partnerNames[latest[0]] || latest[0];
        insights.push({
          type: 'shift',
          text: `${eName} peaks earlier than ${lName} (${latest[1].h - earliest[1].h}h gap).`,
        });
      }
    }
  }

  return insights.slice(0, 3);
};


// ---------------------------------------------------------------------------
// Auto Insights (filter-aware)
// ---------------------------------------------------------------------------

const InsightsCard = ({ insights }) => (
  <Card span={4} testid="block-insights">
    <CardTitle icon={Sparkle} title="Insights" subtitle="Responds to filters" color="text-violet-400" />
    {(!insights || insights.length === 0) ? (
      <div className="text-xs text-zinc-500">Not enough data to generate insights.</div>
    ) : (
      <ul className="space-y-2" data-testid="insights-list">
        {insights.slice(0, 3).map((i, idx) => (
          <li key={idx} className="flex items-start gap-2 text-[11px] text-zinc-300 leading-relaxed" data-testid={`insight-${i.type}`}>
            <span className="mt-1 w-1 h-1 rounded-full bg-violet-400 shrink-0" />
            <span>{i.text}</span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

// ---------------------------------------------------------------------------
// Order Mix Donut (partner-aware)
// ---------------------------------------------------------------------------

const PartnerMixDonut = ({ partnerMix, visiblePartners }) => {
  const chartData = useMemo(
    () => (partnerMix || [])
      .filter((p) => visiblePartners.includes(p.partner_id) && p.orders > 0)
      .map((p) => ({ ...p, color: colorFor(p.partner_id) })),
    [partnerMix, visiblePartners],
  );
  const total = chartData.reduce((a, b) => a + b.orders, 0);

  return (
    <Card span={4} testid="block-mix-donut">
      <CardTitle icon={Receipt} title="Order Mix" subtitle="Share by partner" color="text-emerald-400" />
      {total === 0 ? (
        <div className="text-xs text-zinc-500">No orders in this range.</div>
      ) : (
        <>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="orders" nameKey="partner_name" innerRadius={38} outerRadius={64} paddingAngle={2} stroke="none">
                  {chartData.map((e) => <Cell key={e.partner_id} fill={e.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#09090b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 11 }}
                  formatter={(v, n) => [`${v} orders`, n]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1 max-h-36 overflow-y-auto pr-1">
            {chartData.map((row) => (
              <div key={row.partner_id} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="w-1.5 h-3 rounded-sm shrink-0" style={{ background: row.color }} />
                  <span className="text-zinc-300 truncate">{row.partner_name}</span>
                </span>
                <span className="font-mono text-zinc-400 shrink-0 ml-2">
                  {row.orders} <span className="text-zinc-600">({row.share_pct}%)</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Hourly Heatmap — rows = partner_ids (filtered by mode)
// ---------------------------------------------------------------------------

const HourlyHeatmap = ({ hourlyDemand, visiblePartners, partnerMixById }) => {
  // For each partner → orders per hour
  const rowsData = useMemo(() => {
    const partnerHourly = {};
    for (const pid of visiblePartners) {
      partnerHourly[pid] = Array(24).fill(0);
    }
    for (const h of hourlyDemand) {
      for (const p of h.partners || []) {
        if (visiblePartners.includes(p.partner_id)) {
          partnerHourly[p.partner_id][h.hour] = p.orders;
        }
      }
    }
    return partnerHourly;
  }, [hourlyDemand, visiblePartners]);

  const globalMax = useMemo(() => {
    let m = 0;
    for (const pid of visiblePartners) for (const v of rowsData[pid]) if (v > m) m = v;
    return m || 1;
  }, [rowsData, visiblePartners]);

  return (
    <Card span={8} testid="block-heatmap">
      <CardTitle icon={TrendUp} title="Hourly Density" subtitle="Partner rows · darker = more orders" color="text-orange-400" />
      {visiblePartners.length === 0 ? (
        <div className="text-xs text-zinc-500">No partners to display.</div>
      ) : (
        <div className="space-y-1.5">
          {visiblePartners.map((pid) => {
            const row = rowsData[pid];
            const name = partnerMixById[pid]?.partner_name || pid;
            return (
              <div key={pid} className="flex items-center gap-2" data-testid={`heatmap-row-${pid}`}>
                <div className="w-28 text-[10px] text-zinc-400 flex items-center gap-1.5 shrink-0 truncate" title={name}>
                  <span className="w-1.5 h-3 rounded-sm shrink-0" style={{ background: colorFor(pid) }} />
                  <span className="truncate">{name}</span>
                </div>
                <div className="flex-1 grid gap-[2px]" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                  {row.map((v, idx) => {
                    const intensity = v === 0 ? 0 : 0.15 + (v / globalMax) * 0.85;
                    return (
                      <div
                        key={idx}
                        title={`${fmtHourAmPm(idx)} · ${name}: ${v} orders`}
                        className="h-7 rounded-sm border border-white/[0.03]"
                        style={{
                          background: v === 0 ? 'rgba(255,255,255,0.02)' : colorFor(pid),
                          opacity: v === 0 ? 1 : intensity,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex items-center gap-2 mt-1">
            <div className="w-28 shrink-0" />
            <div className="flex-1 grid gap-[2px] text-[9px] text-zinc-600 text-center font-mono" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
              {Array.from({ length: 24 }).map((_, idx) => (
                <span key={idx}>{idx % 3 === 0 ? idx : ''}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Control strip
// ---------------------------------------------------------------------------

const ControlStrip = ({ activeMode, onModeChange, filters, visiblePartnerCount }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid="time-control-strip">
    <span className="text-[10px] uppercase tracking-wider text-zinc-500">View</span>
    <div className="flex gap-1 bg-white/[0.03] rounded-lg p-1">
      {MODE_TOGGLES.map((m) => {
        const active = activeMode === m;
        const color = SERVICE_COLOR[m];
        return (
          <button
            key={m}
            onClick={() => onModeChange(m)}
            className={`px-3 py-1 text-[11px] font-medium rounded transition-colors ${
              active ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`mode-toggle-${m}`}
            style={active ? { boxShadow: `inset 0 0 0 1px ${color}33` } : {}}
          >
            {SERVICE_LABEL[m]}
          </button>
        );
      })}
    </div>
    <span className="text-[10px] text-zinc-500 font-mono">{visiblePartnerCount} partner stream(s)</span>
    <div className="ml-auto text-[11px] text-zinc-500 font-mono">
      {filters?.outlet_id ? `outlet: ${filters.outlet_id.slice(0, 8)}…` : 'all outlets'}
      {filters?.start_date && filters?.end_date
        ? ` · ${filters.start_date} → ${filters.end_date}`
        : filters?.business_date ? ` · date: ${filters.business_date}` : ''}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TimeOfDayBento = ({ outletId, startDate, endDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState('all');
  const [customWindow, setCustomWindow] = useState({ start: null, end: null });
  const [showPrepOverlay, setShowPrepOverlay] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (outletId) params.append('outlet_id', outletId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (customWindow.start != null && customWindow.end != null) {
        params.append('window_start', String(customWindow.start));
        params.append('window_end', String(customWindow.end));
      }
      const res = await api.get(`/sales/time-intelligence?${params.toString()}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load time intelligence');
    } finally {
      setLoading(false);
    }
  }, [outletId, startDate, endDate, customWindow]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Partner-level derived state
  const allPartners = data?.partner_mix?.map((p) => p.partner_id) || [];
  const partnerServiceModeMap = useMemo(() => {
    const m = {};
    for (const p of data?.partner_mix || []) m[p.partner_id] = p.service_mode;
    return m;
  }, [data]);
  const partnerMixById = useMemo(() => {
    const m = {};
    for (const p of data?.partner_mix || []) m[p.partner_id] = p;
    return m;
  }, [data]);
  const visiblePartners = useMemo(
    () => filterPartnersForMode(allPartners, activeMode, partnerServiceModeMap),
    [allPartners, activeMode, partnerServiceModeMap],
  );

  // Filter-aware insights (client-side regeneration)
  const partnerNames = useMemo(() => {
    const m = {};
    for (const p of data?.partner_mix || []) m[p.partner_id] = p.partner_name;
    return m;
  }, [data]);
  const filteredInsights = useMemo(() => {
    if (!data) return [];
    return buildFilteredInsights({
      hourlyDemand: data.hourly_demand,
      partnerMix: data.partner_mix,
      visiblePartners,
      activeMode,
      windowSummary: data.time_window_summary,
      hourlyPrepTime: data.hourly_prep_time,
      partnerNames,
    });
  }, [data, visiblePartners, activeMode, partnerNames]);

  if (loading) {
    return <div className="py-16 text-center text-zinc-500 text-sm" data-testid="time-bento-loading">Loading time intelligence...</div>;
  }

  if (!data || data.total_orders === 0) {
    return (
      <div className="surface-base rounded-lg py-16 text-center" data-testid="time-bento-empty">
        <Clock size={40} weight="duotone" className="mx-auto text-zinc-600 mb-2" />
        <p className="text-zinc-400 text-sm">No time-of-day data yet.</p>
        <p className="text-zinc-600 text-xs mt-1">Orders need to be ingested with timestamps for this view.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="time-of-day-bento">
      <ControlStrip
        activeMode={activeMode}
        onModeChange={setActiveMode}
        filters={data.filters}
        visiblePartnerCount={visiblePartners.length}
      />

      <div className="grid grid-cols-12 gap-4">
        <HourlyDemandHero
          hourlyDemand={data.hourly_demand}
          hourlyPrepTime={data.hourly_prep_time}
          visiblePartners={visiblePartners}
          partnerMixById={partnerMixById}
          activeMode={activeMode}
          showPrepOverlay={showPrepOverlay}
          onTogglePrepOverlay={() => setShowPrepOverlay((v) => !v)}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <PeakLoadCard
          peakByPartner={data.peak_load_by_partner}
          visiblePartners={visiblePartners}
          overall={data.peak_load?.overall}
        />
        <TimeWindowCard
          summary={data.time_window_summary}
          visiblePartners={visiblePartners}
          isDefaultPeak={data.time_window_summary?.is_default_peak_window}
          onChange={(s, e) => setCustomWindow({ start: s, end: e })}
        />
        <InsightsCard insights={filteredInsights} />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <PartnerMixDonut partnerMix={data.partner_mix} visiblePartners={visiblePartners} />
        <HourlyHeatmap
          hourlyDemand={data.hourly_demand}
          visiblePartners={visiblePartners}
          partnerMixById={partnerMixById}
        />
      </div>
    </div>
  );
};

export default TimeOfDayBento;
